<template>
    <div 
        v-if="loan.early_payments_enabled"
        class="cursor-pointer border rounded-lg bg-gray-50 border-gray-200"
        :class="{ 'mt-4': loan.status === 'current' }"
    >
        <div class="flex justify-between items-center bottom-line p-4">
            <h6 class="text-base font-semibold">Early Payment</h6>
        </div>

        <div>

            <div class="p-4">
                <p class="text-sm text-gray-500">
                    Pay any amount. It will be scheduled for the next business day and applied to future payments.
                </p>

                <p v-if="!canScheduleEarlyPayment" class="text-sm text-warning mt-2">
                    Early payments are enabled, but a new early payment can’t be scheduled right now (one may already be scheduled).
                </p>
            </div>

            <div class="p-4 pt-0 space-y-4">
                <div>
                    <label class="font-semibold text-sm block mb-2" for="early-payment-amount">
                        Payment amount
                    </label>

                    <input
                        id="early-payment-amount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Enter amount"
                        class="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring focus:ring-action"
                        :disabled="!canScheduleEarlyPayment"
                        v-model.number="earlyPaymentAmount"
                    />
                </div>

                <button
                    @click="onConfirmEarlyPayment"
                    type="button"
                    :disabled="!canScheduleEarlyPayment || earlyLoadingConfirm || !earlyPaymentAmount"
                    class="mt-4 block w-full no-underline border border-primary-700 rounded text-primary-700 text-center text-base font-semibold py-3 disabled:opacity-50"
                >
                    <template v-if="!earlyLoadingConfirm">
                        Schedule Payment
                    </template>

                    <template v-else>
                        <i class="fa fa-circle-notch fa-spin" />
                    </template>
                </button>
            </div>

            <div v-if="earlyScheduledMessage" class="text-success text-center pb-4">
                {{ earlyScheduledMessage }}
            </div>

            <div v-if="earlyErrorMessage" class="text-danger text-center pb-4">
                {{ earlyErrorMessage }}
            </div>

        </div>
    </div>
</template>

<script>
const EARLY_PAYMENT_STRATEGY = 'cover_future_installments';
const EARLY_PAYMENT_STRATEGY_REDUCE = 'reduce_principal';

export default {
    name: 'EarlyPaymentForm',

    props: {
        loan: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            earlyPaymentAmount: null,
            earlyPaymentStrategy: EARLY_PAYMENT_STRATEGY,
            earlyLoadingConfirm: false,
            earlyErrorMessage: '',
            earlyScheduledMessage: '',
            earlyRequested: false
        };
    },

    computed: {
        canScheduleEarlyPayment() {
            return Boolean(this.loan?.early_payment_allowed);
        },

        prepaidAmountFormatted() {
            const amount = this.loan?.prepaid_amount ?? 0;
            return this.$options.filters.money(amount, 2);
        },
    },

    methods: {
        onConfirmEarlyPayment() {
            this.confirmEarlyPayment();
        },

        requestEarlyPaymentInfo() {
            this.earlyLoadingConfirm = false;
            this.earlyErrorMessage = '';

            this.$http.post(`/account/loans/${this.loan.id}/early-payment-info`)
                .then(response => {
                    const data = response.data || {};
                    this.earlyPaymentAmount = data.amount ?? null;
                })
                .catch(error => {
                    this.earlyErrorMessage = error.response?.data?.error || error.response?.data?.message || 'An unexpected error occurred';
                });
        },

        resetEarlyPaymentForm() {
            this.earlyPaymentAmount = null;
            this.earlyPaymentStrategy = EARLY_PAYMENT_STRATEGY;
            this.earlyErrorMessage = '';
            this.earlyRequested = false;
            this.earlyScheduledMessage = '';
        },

        confirmEarlyPayment() {
            this.earlyErrorMessage = '';
            const amount = parseFloat(this.earlyPaymentAmount);

            if (!amount || amount <= 0) {
                this.earlyErrorMessage = 'Amount must be greater than zero.';
                return;
            }

            this.earlyLoadingConfirm = true;

            this.$http.post(`/account/loans/${this.loan.id}/confirm-early-payment`, {
                amount,
                strategy: this.earlyPaymentStrategy
            })
                .then(response => {
                    const amountProcessed = Number(response.data.processed_amount ?? amount);
                    this.earlyRequested = true;
                    this.earlyScheduledMessage = this.buildEarlyScheduledMessage(response.data);
                    this.resetEarlyPaymentForm();
                    this.notifyLoanBalanceUpdate(amountProcessed, response.data);
                })
                .catch(error => {
                    this.earlyErrorMessage = error.response?.data?.error || error.response?.data?.message || 'An unexpected error occurred';
                })
                .finally(() => {
                    this.earlyLoadingConfirm = false;
                });
        },

        buildEarlyScheduledMessage(responseData = {}) {
            const amount = responseData.amount ?? this.earlyPaymentAmount;
            const date = responseData.nextBusinessDate ?? null;
            const formattedAmount = amount ? this.$options.filters.money(amount) : '';

            if (formattedAmount && date) {
                return `Scheduled ${formattedAmount} for ${date}.`;
            }

            return 'Early payment scheduled successfully.';
        },

        notifyLoanBalanceUpdate(processedAmount = 0, responseData = {}) {
            if (!processedAmount && responseData.prepaid_total === undefined && responseData.prepaid_stored === undefined && !responseData.nextBusinessDate) {
                return;
            }

            const detail = {
                processedAmount: Number(processedAmount || 0),
                nextBusinessDate: responseData.nextBusinessDate ?? null,
                prepaid_total: responseData.prepaid_total ?? responseData.prepaid_stored,
            };

            window.dispatchEvent(new CustomEvent('loanEarlyPaymentUpdated', { detail }));
        }
    }
};
</script>